import { useEffect, useState } from 'react';
import { PLAN_LABEL, PLAN_NAMES } from '../../components/plan/planLabels';
import { getErrorMessage } from '../../lib/errors';
import { formatTimestamp } from '../../lib/format';
import type { AdminPlanStatsRead } from '../../types/api';
import { fetchPlanStats } from './adminApi';

/** The last completed load, tagged with the refresh it answers. */
interface Loaded {
  key: string;
  stats: AdminPlanStatsRead | null;
  error: string | null;
}

/**
 * A compact strip of deployment-wide numbers above the accounts table:
 * accounts by plan, trials, sign-ups and trial conversions. Its own fetch, so a
 * failure here says so in place and never hides the table. `refreshKey`
 * changes after a plan save so the counts follow the edit.
 */
export function AdminPlanStats({ refreshKey }: { refreshKey: number }) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [nonce, setNonce] = useState(0);
  const key = `${refreshKey}:${nonce}`;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const stats = await fetchPlanStats(controller.signal);
        if (cancelled) return;
        setLoaded({ key, stats, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded({
          key,
          stats: null,
          error: getErrorMessage(caught, 'Could not load the plan numbers.'),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [key]);

  // A refresh after a save keeps showing the previous numbers until the new
  // ones land; only the very first load (or a retry after an error) says Loading.
  const stats = loaded?.stats ?? null;
  const error = loaded !== null && loaded.key === key ? loaded.error : null;

  if (error !== null) {
    return (
      <div className="admin-pane__foot">
        <p className="form__error" role="alert">
          {error}
        </p>
        <button className="button" type="button" onClick={() => setNonce((value) => value + 1)}>
          Try again
        </button>
      </div>
    );
  }

  if (stats === null) {
    return (
      <p className="kb-section__status" role="status">
        Loading plan numbers
      </p>
    );
  }

  const split = PLAN_NAMES.map((plan) => `${PLAN_LABEL[plan]} ${stats.accounts[plan]}`).join(' · ');

  return (
    <section className="admin-stats" aria-label="Plan numbers">
      <dl className="admin-stats__tiles">
        <div className="admin-stats__tile">
          <dt>Accounts</dt>
          <dd>
            <span className="admin-stats__value">{stats.accounts.total}</span>
            <span className="admin-stats__hint">{split}</span>
          </dd>
        </div>
        <div className="admin-stats__tile">
          <dt>Trials active</dt>
          <dd>
            <span className="admin-stats__value">{stats.trials.active}</span>
          </dd>
        </div>
        <div className="admin-stats__tile">
          <dt>Ending in 7 days</dt>
          <dd>
            <span className="admin-stats__value">{stats.trials.ending_7d}</span>
          </dd>
        </div>
        <div className="admin-stats__tile">
          <dt>Trials ended</dt>
          <dd>
            <span className="admin-stats__value">{stats.trials.ended}</span>
          </dd>
        </div>
        <div className="admin-stats__tile">
          <dt>Sign-ups 7d</dt>
          <dd>
            <span className="admin-stats__value">{stats.signups.last_7d}</span>
          </dd>
        </div>
        <div className="admin-stats__tile">
          <dt>Sign-ups 30d</dt>
          <dd>
            <span className="admin-stats__value">{stats.signups.last_30d}</span>
          </dd>
        </div>
        <div className="admin-stats__tile">
          <dt>Converted</dt>
          <dd>
            <span className="admin-stats__value">{stats.converted_from_trial}</span>
            <span className="admin-stats__hint">from trial</span>
          </dd>
        </div>
      </dl>
      <p className="admin-stats__asof">As of {formatTimestamp(stats.as_of)}</p>
    </section>
  );
}
